import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Search, Check, Printer, Package, ArrowRightLeft, MapPin, FileText, Download, Trash2, Edit2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import BarcodeInput from '@/components/BarcodeInput';
import ScanFeedbackOverlay from '@/components/ScanFeedbackOverlay';
import apiClient from '@/services/apiClient';

const FGDesignTransfer = () => {
  const navigate = useNavigate();
  const targetSkuRef = useRef(null);
  const usnInputRef = useRef(null);

  // State
  const [step, setStep] = useState('sku'); // 'sku' or 'usn'
  const [targetSkuInput, setTargetSkuInput] = useState('');
  const [targetSku, setTargetSku] = useState(null);
  const [suggestedBin, setSuggestedBin] = useState(null);
  const [usnInput, setUsnInput] = useState('');
  const [scannedItems, setScannedItems] = useState([]); // staged USNs (not yet transferred)
  const [transferredItems, setTransferredItems] = useState([]);
  const [pendingLabels, setPendingLabels] = useState([]);
  const [isTransferring, setIsTransferring] = useState(false); // per-scan validation
  const [isBatchTransferring, setIsBatchTransferring] = useState(false);
  const [batchFailures, setBatchFailures] = useState([]);
  const [scanFeedback, setScanFeedback] = useState(null);
  const [errors, setErrors] = useState({});
  
  // Bin override state
  const [showBinOverrideDialog, setShowBinOverrideDialog] = useState(false);
  const [binOverrideInput, setBinOverrideInput] = useState('');
  const [isValidatingBin, setIsValidatingBin] = useState(false);
  const [binOverrideError, setBinOverrideError] = useState('');

  // Computed
  const transferredSet = useMemo(() => new Set(transferredItems.map(i => i.usn_code)), [transferredItems]);
  const scannedSet = useMemo(() => new Set(scannedItems.map(i => i.usn_code)), [scannedItems]);

  // Auto-focus
  useEffect(() => {
    if (step === 'sku' && targetSkuRef.current) {
      targetSkuRef.current.focus();
    } else if (step === 'usn' && usnInputRef.current) {
      usnInputRef.current.focus();
    }
  }, [step]);

  const showFeedback = (type, message) => {
    setScanFeedback({ type, message });
    setTimeout(() => setScanFeedback(null), 350);
  };

  const handleTargetSkuScan = async (value) => {
    if (!value || value.trim() === '') {
      setErrors({ ...errors, sku: 'Target SKU is required' });
      showFeedback('error', 'SKU Required');
      return;
    }

    try {
      const response = await apiClient.get(`/fg-design-transfer/lookup-sku/${encodeURIComponent(value.trim())}`);
      
      if (!response.data.found) {
        setErrors({ ...errors, sku: response.data.message });
        showFeedback('error', 'SKU Not Found');
        setTargetSkuInput('');
        return;
      }

      setTargetSku(response.data.sku);
      setSuggestedBin(response.data.bin_suggestion);
      setTargetSkuInput('');
      setErrors({ ...errors, sku: null });
      showFeedback('success', `SKU: ${response.data.sku.design_name}`);
      setStep('usn');
      
      const binMsg = response.data.bin_suggestion?.bin_code 
        ? `Suggested bin: ${response.data.bin_suggestion.bin_code}` 
        : 'No bin suggestion available';
      toast.success(`Target SKU: ${response.data.sku.design_name}/${response.data.sku.shade}. ${binMsg}`);
    } catch (error) {
      console.error('SKU lookup error:', error);
      setErrors({ ...errors, sku: 'Failed to lookup SKU' });
      showFeedback('error', 'Lookup Error');
      setTargetSkuInput('');
    }
  };

  const handleUsnScan = async (value) => {
    if (!value || value.trim() === '') {
      setErrors({ ...errors, usn: 'USN code is required' });
      showFeedback('error', 'USN Required');
      return;
    }

    const code = value.trim();

    if (scannedSet.has(code) || transferredSet.has(code)) {
      setErrors({ ...errors, usn: 'USN already scanned in this batch' });
      showFeedback('error', 'Already Scanned');
      setUsnInput('');
      return;
    }

    if (!suggestedBin?.bin_code) {
      toast.error('No target bin available. Please select a bin first.');
      return;
    }

    try {
      setIsTransferring(true);

      // Validate only (fast, indexed). NO transfer here — we batch on submit.
      const validateResponse = await apiClient.get(`/fg-design-transfer/validate-usn/${encodeURIComponent(code)}`);

      if (!validateResponse.data.valid) {
        setErrors({ ...errors, usn: validateResponse.data.message });
        showFeedback('error', 'Invalid USN');
        setUsnInput('');
        return;
      }

      // Skip if already on the target SKU (no transfer needed)
      if (validateResponse.data.current_barcode === targetSku.barcode) {
        setErrors({ ...errors, usn: 'USN already has the target SKU' });
        showFeedback('error', 'Same SKU');
        setUsnInput('');
        return;
      }

      setScannedItems(prev => [{
        usn_code: validateResponse.data.usn_code || code,
        current_barcode: validateResponse.data.current_barcode,
        current_design: validateResponse.data.current_design,
        current_shade: validateResponse.data.current_shade,
        current_bin: validateResponse.data.current_bin,
      }, ...prev]);

      setUsnInput('');
      setErrors({ ...errors, usn: null });
      showFeedback('success', 'Added!');

      setTimeout(() => {
        if (usnInputRef.current) usnInputRef.current.focus();
      }, 80);
    } catch (error) {
      console.error('Validation error:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to validate USN';
      setErrors({ ...errors, usn: errorMsg });
      showFeedback('error', 'Scan Failed');
      toast.error(errorMsg);
      setUsnInput('');
    } finally {
      setIsTransferring(false);
    }
  };

  const handleRemoveScanned = (usnCode) => {
    setScannedItems(prev => prev.filter(i => i.usn_code !== usnCode));
  };

  const handleTransferBatch = async () => {
    if (scannedItems.length === 0) {
      toast.info('Scan at least one USN first');
      return;
    }
    if (!suggestedBin?.bin_code) {
      toast.error('No target bin selected');
      return;
    }

    try {
      setIsBatchTransferring(true);
      setBatchFailures([]);

      const response = await apiClient.post('/fg-design-transfer/transfer-batch', {
        usn_codes: scannedItems.map(i => i.usn_code),
        target_barcode: targetSku.barcode,
        target_bin_code: suggestedBin.bin_code,
      });

      if (response.data.success) {
        const transferred = response.data.transferred || [];
        const failures = response.data.failures || [];

        setTransferredItems(prev => [
          ...transferred.map(t => ({ ...t, timestamp: new Date().toISOString() })),
          ...prev,
        ]);
        setPendingLabels(prev => [...prev, ...transferred.map(t => ({ usn_code: t.usn_code }))]);
        setBatchFailures(failures);
        setScannedItems([]); // clear staged list

        setSuggestedBin(prev => ({ ...prev, current_qty: response.data.target_bin_qty }));

        if (failures.length > 0) {
          toast.warning(`Transferred ${transferred.length}, ${failures.length} failed (see list)`);
        } else {
          toast.success(`Transferred ${transferred.length} USN(s). Ready to print labels.`);
        }

        setTimeout(() => {
          if (usnInputRef.current) usnInputRef.current.focus();
        }, 100);
      }
    } catch (error) {
      console.error('Batch transfer error:', error);
      toast.error(error.response?.data?.detail || 'Batch transfer failed');
    } finally {
      setIsBatchTransferring(false);
    }
  };

  const handlePrintLabels = async () => {
    if (pendingLabels.length === 0) {
      toast.info('No labels to print');
      return;
    }
    
    try {
      // Get USN codes from pending labels
      const usnCodes = pendingLabels.map(item => item.usn_code);
      
      const response = await apiClient.post('/main-labels/design-transfer/print-labels', {
        usn_codes: usnCodes,
        printer_type: 'usb'
      });
      
      if (response.data.success) {
        toast.success(`${response.data.labels.length} labels ready for printing!`);
        
        // Clear pending labels after successful print
        setPendingLabels([]);
        
        // Navigate to USB printing page with the label data
        navigate('/wms/fg/usn-label-reprint', { 
          state: { 
            designTransferLabels: response.data.labels,
            fromDesignTransfer: true
          } 
        });
      }
    } catch (error) {
      console.error('Error printing labels:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate labels');
    }
  };

  const handleNewTransfer = () => {
    setStep('sku');
    setTargetSkuInput('');
    setTargetSku(null);
    setSuggestedBin(null);
    setUsnInput('');
    setScannedItems([]);
    setTransferredItems([]);
    setPendingLabels([]);
    setBatchFailures([]);
    setErrors({});
  };

  const handleChangeSku = () => {
    setStep('sku');
    setTargetSku(null);
    setSuggestedBin(null);
  };

  const handleOpenBinOverride = () => {
    setBinOverrideInput('');
    setBinOverrideError('');
    setShowBinOverrideDialog(true);
  };

  const handleBinOverride = async () => {
    if (!binOverrideInput || binOverrideInput.trim() === '') {
      setBinOverrideError('Please enter a bin code');
      return;
    }

    setIsValidatingBin(true);
    setBinOverrideError('');

    try {
      // Validate the bin exists and get its info
      const response = await apiClient.get(`/wms/bins/validate/${encodeURIComponent(binOverrideInput.trim())}`);
      
      if (!response.data.valid) {
        setBinOverrideError(response.data.message || 'Invalid bin code');
        return;
      }

      const binData = response.data.bin;
      
      // Check if bin has different SKU (warning only, not blocking)
      if (binData.current_barcode && binData.current_barcode !== targetSku?.barcode) {
        // Allow override but show warning
        toast.warning(`Warning: Bin ${binData.bin_code} contains a different SKU (${binData.design_name}). Proceeding will mix SKUs.`);
      }

      // Update the suggested bin with the overridden one
      setSuggestedBin({
        bin_code: binData.bin_code,
        hall: binData.hall,
        row: binData.row,
        column: binData.column,
        shelf: binData.shelf,
        side: binData.side,
        current_qty: binData.current_qty || 0,
        capacity: binData.capacity,
        suggestion_type: 'manual_override',
        message: `Manually selected bin ${binData.bin_code}`,
        is_manual_override: true
      });

      setShowBinOverrideDialog(false);
      toast.success(`Bin changed to ${binData.bin_code}`);
      
      // Focus back on USN input
      setTimeout(() => {
        if (usnInputRef.current) {
          usnInputRef.current.focus();
        }
      }, 100);
    } catch (error) {
      console.error('Bin validation error:', error);
      setBinOverrideError(error.response?.data?.detail || 'Failed to validate bin');
    } finally {
      setIsValidatingBin(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="fg-design-transfer">
      <ScanFeedbackOverlay feedback={scanFeedback} />
      
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/wms/fg')} 
              className="text-[#F9FAFB] hover:bg-white/10"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display flex items-center gap-2">
                <ArrowRightLeft className="w-6 h-6" />
                FG Design Transfer
              </h1>
              <p className="text-sm text-[#E8DCC8]">Transfer USNs from one SKU to another</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {pendingLabels.length > 0 && (
              <Button 
                onClick={handlePrintLabels}
                variant="outline"
                className="bg-white/10 border-white/20 text-white hover:bg-white/20"
              >
                <Printer className="w-4 h-4 mr-2" />
                Print Labels ({pendingLabels.length})
              </Button>
            )}
            {transferredItems.length > 0 && (
              <Button 
                onClick={handleNewTransfer}
                variant="outline"
                className="bg-white/10 border-white/20 text-white hover:bg-white/20"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                New Transfer
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 md:px-6 py-6 space-y-6">
        {/* Step 1: Enter Target SKU */}
        {step === 'sku' && (
          <Card className="border-[#E5DFD0]">
            <CardHeader>
              <CardTitle className="text-[#1A1F2E] flex items-center gap-2">
                <span className="bg-[#2D5F4F] text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">1</span>
                Enter Target SKU (Barcode)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600 mb-4">
                Enter the SKU barcode you want to transfer inventory TO (e.g., "Ambadi 12345")
              </p>
              <BarcodeInput
                ref={targetSkuRef}
                value={targetSkuInput}
                onChange={setTargetSkuInput}
                onScan={handleTargetSkuScan}
                placeholder="Scan or enter target SKU barcode"
                error={errors.sku}
                data-testid="target-sku-input"
              />
            </CardContent>
          </Card>
        )}

        {/* Step 2: Scan USNs (after SKU is selected) */}
        {step === 'usn' && targetSku && (
          <>
            {/* Target SKU Info */}
            <Card className="bg-green-50 border-green-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="bg-green-100 p-3 rounded-lg">
                      <Package className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-green-600 font-medium">Target SKU</p>
                      <p className="text-xl font-bold text-green-800">{targetSku.design_name}</p>
                      <p className="text-sm text-green-600">
                        Shade: {targetSku.shade || '-'} | Size: {targetSku.size || '-'}
                      </p>
                      <p className="text-xs text-green-500 font-mono mt-1">{targetSku.barcode}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-green-600">MRP</p>
                    <p className="text-xl font-bold text-green-800">₹{targetSku.mrp || 0}</p>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={handleChangeSku}
                      className="text-green-600 hover:text-green-700 mt-1"
                    >
                      Change SKU
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Suggested Bin Info */}
            {suggestedBin && suggestedBin.bin_code && (
              <Card className={`${suggestedBin.is_manual_override ? 'bg-purple-50 border-purple-200' : 'bg-blue-50 border-blue-200'}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`${suggestedBin.is_manual_override ? 'bg-purple-100' : 'bg-blue-100'} p-3 rounded-lg`}>
                        <MapPin className={`w-6 h-6 ${suggestedBin.is_manual_override ? 'text-purple-600' : 'text-blue-600'}`} />
                      </div>
                      <div>
                        <p className={`text-sm ${suggestedBin.is_manual_override ? 'text-purple-600' : 'text-blue-600'} font-medium`}>
                          {suggestedBin.is_manual_override 
                            ? 'Manually Selected Bin' 
                            : suggestedBin.suggestion_type === 'existing_sku' 
                              ? 'Auto-Suggested Bin (Same SKU)' 
                              : 'Suggested Bin (Empty)'}
                        </p>
                        <p className={`text-xl font-bold ${suggestedBin.is_manual_override ? 'text-purple-800' : 'text-blue-800'}`}>
                          {suggestedBin.bin_code}
                        </p>
                        <p className={`text-xs ${suggestedBin.is_manual_override ? 'text-purple-600' : 'text-blue-600'}`}>
                          {suggestedBin.message}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-sm ${suggestedBin.is_manual_override ? 'text-purple-600' : 'text-blue-600'}`}>Current Items</p>
                      <p className={`text-2xl font-bold ${suggestedBin.is_manual_override ? 'text-purple-800' : 'text-blue-800'}`}>
                        {suggestedBin.current_qty || 0}
                      </p>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={handleOpenBinOverride}
                        className={`${suggestedBin.is_manual_override ? 'text-purple-600 hover:text-purple-700' : 'text-blue-600 hover:text-blue-700'} mt-1`}
                        data-testid="change-bin-btn"
                      >
                        <Edit2 className="w-3 h-3 mr-1" />
                        Change Bin
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* No Bin Warning */}
            {suggestedBin && !suggestedBin.bin_code && (
              <Card className="bg-amber-50 border-amber-200">
                <CardContent className="p-4">
                  <p className="text-amber-800">
                    <strong>⚠️ No Suitable Bin:</strong> {suggestedBin.message}. 
                    Please create an empty bin first or free up a bin.
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Scan USN Card */}
            {suggestedBin?.bin_code && (
              <Card className="border-[#E5DFD0]">
                <CardHeader className="pb-3">
                  <CardTitle className="text-[#1A1F2E] flex items-center gap-2">
                    <span className="bg-[#2D5F4F] text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">2</span>
                    Scan USNs to Transfer
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600 mb-4">
                    Scan all USNs from the <strong>source SKU</strong>, then click <strong>Transfer Batch</strong> to move them to <strong>{targetSku.design_name}/{targetSku.shade}</strong> in one go.
                  </p>
                  <BarcodeInput
                    ref={usnInputRef}
                    value={usnInput}
                    onChange={setUsnInput}
                    onScan={handleUsnScan}
                    placeholder="Scan USN code"
                    error={errors.usn}
                    disabled={isTransferring || isBatchTransferring}
                    data-testid="usn-input"
                  />
                  {isTransferring && (
                    <div className="flex items-center gap-2 mt-2 text-sm text-gray-500">
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Validating...
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Staged (scanned, not yet transferred) list + Transfer Batch button */}
            {scannedItems.length > 0 && (
              <Card className="border-[#2D5F4F]/30 bg-[#F4FBF7]" data-testid="staged-scans-card">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-[#1A1F2E] flex items-center gap-2">
                      <ArrowRightLeft className="w-5 h-5 text-[#2D5F4F]" />
                      Scanned & Ready ({scannedItems.length})
                    </CardTitle>
                    <Button
                      onClick={handleTransferBatch}
                      disabled={isBatchTransferring}
                      className="bg-[#2D5F4F] hover:bg-[#234a3d] text-white"
                      data-testid="transfer-batch-btn"
                    >
                      {isBatchTransferring ? (
                        <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Transferring {scannedItems.length}…</>
                      ) : (
                        <><Check className="w-4 h-4 mr-2" /> Transfer Batch ({scannedItems.length})</>
                      )}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="rounded-md border overflow-x-auto max-h-[300px] overflow-y-auto">
                    <Table>
                      <TableHeader className="sticky top-0 bg-white">
                        <TableRow>
                          <TableHead className="w-[50px]">#</TableHead>
                          <TableHead>USN Code</TableHead>
                          <TableHead>Current SKU</TableHead>
                          <TableHead>Current Bin</TableHead>
                          <TableHead className="w-[60px]"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {scannedItems.map((item, idx) => (
                          <TableRow key={item.usn_code} data-testid={`staged-row-${item.usn_code}`}>
                            <TableCell className="text-gray-500">{scannedItems.length - idx}</TableCell>
                            <TableCell className="font-mono font-medium text-sm">{item.usn_code}</TableCell>
                            <TableCell>
                              <div className="text-sm">
                                <p className="font-medium">{item.current_design || '-'}</p>
                                <p className="text-gray-500 text-xs">{item.current_shade || ''}</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="font-mono text-xs">{item.current_bin || '-'}</Badge>
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveScanned(item.usn_code)}
                                disabled={isBatchTransferring}
                                className="text-red-500 hover:text-red-700 h-8 w-8 p-0"
                                data-testid={`remove-scan-${item.usn_code}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Batch failures */}
            {batchFailures.length > 0 && (
              <Card className="bg-red-50 border-red-200" data-testid="batch-failures-card">
                <CardContent className="p-4">
                  <p className="font-semibold text-red-800 mb-2">{batchFailures.length} USN(s) could not be transferred:</p>
                  <ul className="text-sm text-red-700 space-y-1 max-h-[150px] overflow-y-auto">
                    {batchFailures.map(f => (
                      <li key={f.usn_code}><span className="font-mono">{f.usn_code}</span> — {f.error}</li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Transfer Summary */}
            <div className="grid grid-cols-3 gap-4">
              <Card className="bg-green-50 border-green-200">
                <CardContent className="p-4 text-center">
                  <ArrowRightLeft className="w-6 h-6 mx-auto text-green-600 mb-1" />
                  <p className="text-2xl font-bold text-green-800">{scannedItems.length}</p>
                  <p className="text-xs text-green-600">Scanned (Pending)</p>
                </CardContent>
              </Card>
              <Card className="bg-purple-50 border-purple-200">
                <CardContent className="p-4 text-center">
                  <Check className="w-6 h-6 mx-auto text-purple-600 mb-1" />
                  <p className="text-2xl font-bold text-purple-800">{transferredItems.length}</p>
                  <p className="text-xs text-purple-600">USNs Transferred</p>
                </CardContent>
              </Card>
              <Card className="bg-orange-50 border-orange-200">
                <CardContent className="p-4 text-center">
                  <Printer className="w-6 h-6 mx-auto text-orange-600 mb-1" />
                  <p className="text-2xl font-bold text-orange-800">{pendingLabels.length}</p>
                  <p className="text-xs text-orange-600">Labels to Print</p>
                </CardContent>
              </Card>
            </div>

            {/* Transferred Items Table */}
            {transferredItems.length > 0 && (
              <Card className="border-[#E5DFD0]">
                <CardHeader className="pb-3">
                  <CardTitle className="text-[#1A1F2E]">Transferred Items ({transferredItems.length})</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="rounded-md border overflow-x-auto max-h-[400px] overflow-y-auto">
                    <Table>
                      <TableHeader className="sticky top-0 bg-gray-50">
                        <TableRow>
                          <TableHead className="w-[50px]">#</TableHead>
                          <TableHead>USN Code</TableHead>
                          <TableHead>From (Design/Shade)</TableHead>
                          <TableHead>To (Design/Shade)</TableHead>
                          <TableHead>Target Bin</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {transferredItems.map((item, idx) => (
                          <TableRow key={item.usn_code} className="bg-green-50/50">
                            <TableCell className="text-gray-500">{transferredItems.length - idx}</TableCell>
                            <TableCell className="font-mono font-medium text-sm">{item.usn_code}</TableCell>
                            <TableCell>
                              <div className="text-sm">
                                <p className="font-medium">{item.original_design}</p>
                                <p className="text-gray-500 text-xs">{item.original_shade}</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="text-sm">
                                <p className="font-medium text-green-700">{item.target_design}</p>
                                <p className="text-green-600 text-xs">{item.target_shade}</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge className="bg-blue-100 text-blue-800 font-mono text-xs">
                                {item.target_bin}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </main>

      {/* Bin Override Dialog */}
      <Dialog open={showBinOverrideDialog} onOpenChange={setShowBinOverrideDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-blue-600" />
              Change Target Bin
            </DialogTitle>
            <DialogDescription>
              Enter or scan a different bin code to override the suggested bin.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="bin-override">Bin Code</Label>
              <Input
                id="bin-override"
                value={binOverrideInput}
                onChange={(e) => {
                  setBinOverrideInput(e.target.value.toUpperCase());
                  setBinOverrideError('');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleBinOverride();
                  }
                }}
                placeholder="Scan or enter bin code (e.g., H06-F-32-C-FRONT)"
                className={binOverrideError ? 'border-red-500' : ''}
                autoFocus
                data-testid="bin-override-input"
              />
              {binOverrideError && (
                <p className="text-sm text-red-600">{binOverrideError}</p>
              )}
            </div>
            
            {suggestedBin && (
              <div className="bg-gray-50 p-3 rounded-lg text-sm">
                <p className="text-gray-600">Current bin:</p>
                <p className="font-semibold text-gray-800">{suggestedBin.bin_code}</p>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowBinOverrideDialog(false)}
              disabled={isValidatingBin}
            >
              Cancel
            </Button>
            <Button
              onClick={handleBinOverride}
              disabled={isValidatingBin || !binOverrideInput.trim()}
              className="bg-[#2D5F4F] hover:bg-[#234a3d]"
              data-testid="confirm-bin-override-btn"
            >
              {isValidatingBin ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Validating...
                </>
              ) : (
                'Confirm Bin'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FGDesignTransfer;
